import type { DragEndEvent } from '@dnd-kit/core';
import { AnnouncementBanner } from '../components/AnnouncementBanner';
import { WhatsNewBanner } from '../components/WhatsNewBanner';
import { Skeleton } from '../components/Skeleton';
import { AllAppsGrid } from './blocks/AllAppsGrid';
import { AllAppsLink } from './blocks/AllAppsLink';
import { PinnedApps } from './blocks/PinnedApps';
import {
  STANDALONE_BLOCK_COMPONENTS,
  COMPACT_VARIANTS,
} from './blocks/registry';
import type { BlockId, CustomBlockConfig, HomeVariant } from './blocks/types';
import {
  CLASSIC_BLOCKS,
  COZY_BLOCKS,
  FOCUSED_BLOCKS,
} from './variants/presets';
import type {
  AnnouncementRecord,
  AppRecord,
  MeResponse,
} from '../lib/types';

export interface BlockContext {
  me: { data: MeResponse | null | undefined; loading: boolean };
  apps: { data: AppRecord[] | null | undefined; loading: boolean; error: unknown };
  announcements: { data: AnnouncementRecord[] | null | undefined; loading: boolean };
  pinnedApps: AppRecord[];
  pinnedSet: Set<string>;
  appsByCategory: Array<{ category: string; apps: AppRecord[] }>;
  busyId: string | null;
  readOnly: boolean;
  togglePin: (app: AppRecord) => void;
  onDragEnd: (e: DragEndEvent) => void;
  setRequestApp: (app: AppRecord | null) => void;
  homeView: 'all' | 'by-category';
  setHomeView: (next: 'all' | 'by-category') => void;
}

interface Props {
  variant: HomeVariant;
  customLayout: CustomBlockConfig[];
  ctx: BlockContext;
}

const renderBlock = (id: BlockId, ctx: BlockContext, compact = false): React.ReactNode => {
  const Standalone = compact
    ? COMPACT_VARIANTS[id] ?? STANDALONE_BLOCK_COMPONENTS[id]
    : STANDALONE_BLOCK_COMPONENTS[id];
  if (Standalone) return <Standalone key={id} />;

  // Context-bound blocks
  switch (id) {
    case 'announcements':
      if (ctx.announcements.loading) return <Skeleton key={id} className="h-20 w-full" />;
      if (!ctx.announcements.data || ctx.announcements.data.length === 0) return null;
      return <AnnouncementBanner key={id} items={ctx.announcements.data} />;

    case 'whats-new':
      return <WhatsNewBanner key={id} me={ctx.me.data ?? null} />;

    case 'pinned':
      return (
        <PinnedApps
          key={id}
          pinnedApps={ctx.pinnedApps}
          loading={ctx.me.loading || ctx.apps.loading}
          busyId={ctx.busyId}
          readOnly={ctx.readOnly}
          onTogglePin={ctx.togglePin}
          onRequestAccess={ctx.setRequestApp}
          onDragEnd={ctx.onDragEnd}
        />
      );

    case 'all-apps-link':
      return <AllAppsLink key={id} count={ctx.apps.data?.length ?? 0} />;

    case 'all-apps-grid':
      return (
        <AllAppsGrid
          key={id}
          apps={ctx.apps.data ?? []}
          appsByCategory={ctx.appsByCategory}
          pinnedSet={ctx.pinnedSet}
          busyId={ctx.busyId}
          loading={ctx.apps.loading}
          error={ctx.apps.error}
          readOnly={ctx.readOnly}
          homeView={ctx.homeView}
          onChangeHomeView={ctx.setHomeView}
          onTogglePin={ctx.togglePin}
          onRequestAccess={ctx.setRequestApp}
        />
      );

    default:
      return null;
  }
};

// Wrap each rendered block in a staggered fade-up. The stagger caps at ~600ms
// so even a 12-block dashboard finishes its entrance under a second.
const renderList = (ids: BlockId[], ctx: BlockContext, compact = false) =>
  ids.map((id, i) => {
    const node = renderBlock(id, ctx, compact);
    if (!node) return null;
    return (
      <div
        key={id}
        className="block-fade-up"
        style={{ animationDelay: `${Math.min(i * 60, 600)}ms` }}
      >
        {node}
      </div>
    );
  });

export const HomeVariantRenderer = ({ variant, customLayout, ctx }: Props) => {
  if (variant === 'cozy') {
    return <div className="space-y-6">{renderList(COZY_BLOCKS, ctx)}</div>;
  }
  if (variant === 'focused') {
    return <div className="space-y-6">{renderList(FOCUSED_BLOCKS, ctx, true)}</div>;
  }
  if (variant === 'classic') {
    return <div className="space-y-8">{renderList(CLASSIC_BLOCKS, ctx)}</div>;
  }
  // Custom — only render visible blocks, in user's order
  const visible = customLayout.filter((b) => b.visible).map((b) => b.id);
  return <div className="space-y-6">{renderList(visible, ctx)}</div>;
};
